/**
 * 双嵌套策略
 *
 * 用户选择高级嵌套（nesting_version=2）时，同时发起普通嵌套（nesting_version=1）
 * 以及未开启异形嵌套（不传 nesting_version 且板件不带 cutCurve）两份请求，
 * 结果回来后按材质逐个对比 stockCount（大板用量），取用量更少的版本。
 *
 * 失败策略：任一方案失败（传输层失败 / 轮询超时耗尽 code 6）即整体按失败处理，
 * 不做静默降级；仅后端业务错误（如 code 2）仍只丢弃该方案、继续对比。
 */

/** 嵌套方案标签 */
export const NESTING_TAG = {
  /** 高级嵌套 nesting_version = 2 */
  SENIOR: 'senior',
  /** 普通嵌套 nesting_version = 1 */
  PRIMARY: 'primary',
  /** 未开启异形嵌套：不传 nesting_version，且板件不带 cutCurve */
  OFF: 'off',
}

/** 同用量时的优选顺序：用户所选的高级嵌套 > 普通嵌套 > 未开启嵌套 */
const TAG_PRIORITY = [NESTING_TAG.SENIOR, NESTING_TAG.PRIMARY, NESTING_TAG.OFF]

/**
 * 解析 param 中 layout_data 的 layoutConfig
 * @param {object} data - param.data
 * @returns {object|null} layoutConfig 对象
 */
function parseLayoutConfig(data) {
  try {
    const layoutData = JSON.parse(data.layout_data)
    return layoutData.layoutConfig || null
  } catch {
    return null
  }
}

/**
 * 是否需要启用双嵌套
 * 需同时满足：nesting_version === 2 且 layoutRectMap 中存在带 cutCurve（异形路径）的板件
 * @param {Array} paramArr - 已按刀径/焊接拆分的参数数组
 * @returns {boolean}
 */
export function isDualNestingEnabled(paramArr) {
  return paramArr.some(({ data }) => {
    const layoutConfig = parseLayoutConfig(data)
    if (layoutConfig?.nesting_version !== 2) return false

    try {
      const layoutData = JSON.parse(data.layout_data)
      const layoutRectMap = layoutData.layoutRectMap || {}
      return Object.values(layoutRectMap).some((planks) =>
        planks.some((plank) => plank.cutCurve && plank.cutCurve.length > 0)
      )
    } catch {
      return false
    }
  })
}

/**
 * 去除板件上的 cutCurve（异形轮廓），用于构造「未开启异形嵌套」的排版参数
 * 虚拟组合板等板件的子板件同样需要处理
 * @param {Array} planks - 同一材质下的板件数组
 * @returns {Array} 新的板件数组（不改动原对象）
 */
function stripCutCurve(planks) {
  if (!Array.isArray(planks)) return planks
  return planks.map((plank) => {
    const newPlank = { ...plank }
    delete newPlank.cutCurve
    if (Array.isArray(newPlank.children) && newPlank.children.length) {
      newPlank.children = stripCutCurve(newPlank.children)
    }
    return newPlank
  })
}

/**
 * 去除 layoutRectMap 中所有板件的 cutCurve（异形轮廓）
 * @param {object} layoutRectMap - layout_data.layoutRectMap
 * @returns {object} 新的 layoutRectMap（不改动原对象）
 */
function stripCutCurveFromRectMap(layoutRectMap) {
  if (!layoutRectMap) return layoutRectMap
  return Object.keys(layoutRectMap).reduce((acc, plankType) => {
    acc[plankType] = stripCutCurve(layoutRectMap[plankType])
    return acc
  }, {})
}

/**
 * 扩展参数：每个高级嵌套 param 克隆出普通嵌套、未开启嵌套的副本
 * @param {Array} paramArr - 原始参数数组，元素格式 { data, type? }
 * @param {string} type - 排版方式
 * @returns {Array} 扩展后的参数数组，每个元素附加 _tag: 'senior' | 'primary' | 'off'
 */
export function expandParamsForDualNesting(paramArr, type) {
  return paramArr.flatMap(({ data, type: currentType }) => {
    const actualType = currentType || type
    const layoutData = JSON.parse(data.layout_data)
    const isSenior = layoutData.layoutConfig?.nesting_version === 2

    if (!isSenior) {
      return [{ data, type: actualType, _tag: NESTING_TAG.SENIOR }]
    }

    // 普通嵌套：仅把 nesting_version 改为 1，仍保留异形轮廓 cutCurve
    const primaryLayoutData = {
      ...layoutData,
      layoutConfig: {
        ...layoutData.layoutConfig,
        nesting_version: 1,
      },
    }
    const primaryData = {
      ...data,
      layout_data: JSON.stringify(primaryLayoutData),
    }

    // 未开启异形嵌套：不传 nesting_version，同时去除板件的异形轮廓 cutCurve
    const offLayoutConfig = { ...layoutData.layoutConfig }
    delete offLayoutConfig.nesting_version
    const offLayoutData = {
      ...layoutData,
      layoutConfig: offLayoutConfig,
      layoutRectMap: stripCutCurveFromRectMap(layoutData.layoutRectMap),
    }
    const offData = {
      ...data,
      layout_data: JSON.stringify(offLayoutData),
    }

    return [
      { data, type: actualType, _tag: NESTING_TAG.SENIOR },
      { data: primaryData, type: actualType, _tag: NESTING_TAG.PRIMARY },
      { data: offData, type: actualType, _tag: NESTING_TAG.OFF },
    ]
  })
}

/**
 * 合并多方案结果：按材质对比 stockCount，取大板用量更少的版本
 * 用量相同时按 TAG_PRIORITY 取优先级更高的方案（高级嵌套 > 普通嵌套 > 未开启嵌套）
 *
 * 输出为单个 async_paiban 响应格式，后续 callBack / dealAlignedPlan / sortPlans 等零改动
 *
 * @param {Array} results - 轮询完成后的所有结果（每个含 code、result_data、task_id）
 * @param {Map} taskIdToTag - task_id -> 'senior' | 'primary' | 'off' 映射
 * @returns {Array} 合并后的结果数组，格式同原始 async_paiban 响应
 */
export function mergeDualNestingResults(results, taskIdToTag) {
  const resultsByTag = {
    [NESTING_TAG.SENIOR]: [],
    [NESTING_TAG.PRIMARY]: [],
    [NESTING_TAG.OFF]: [],
  }
  results.forEach((res) => {
    const taskId = res.data?.task_id || res.task_id
    const tag = taskIdToTag.get(taskId)
    resultsByTag[tag]
      ? resultsByTag[tag].push(res)
      : resultsByTag[NESTING_TAG.SENIOR].push(res)
  })

  /** 每个方案下按材质合并后的结果 */
  const mergedByTag = {}
  const allMaterials = new Set()
  TAG_PRIORITY.forEach((tag) => {
    mergedByTag[tag] = resultsByTag[tag].reduce(
      (pre, cut) => ({ ...pre, ...(cut.result_data || {}) }),
      {}
    )
    Object.keys(mergedByTag[tag]).forEach((mat) => allMaterials.add(mat))
  })

  const getCount = (param) => {
    const { data, stockCount } = param
    if (stockCount) return stockCount
    return data.map((v) => new Set(v.map((d) => d.stockNum)).size)
  }

  const finalMerged = {}
  allMaterials.forEach((mat) => {
    let bestParam = null
    let bestMinStock = Infinity
    TAG_PRIORITY.forEach((tag) => {
      const param = mergedByTag[tag][mat]
      if (!param) return
      const minStock = Math.min(...getCount(param))
      // 用量更少者胜出；用量相同则保留优先级更高的方案
      if (minStock < bestMinStock) {
        bestMinStock = minStock
        bestParam = param
      }
    })
    if (bestParam) finalMerged[mat] = bestParam
  })

  if (Object.keys(finalMerged).length === 0) {
    return results
  }

  return [{ code: 1, result_data: finalMerged }]
}

/**
 * 判断单个排版任务是否失败
 * 失败定义：传输层失败（响应没有 code，底层请求出错时 asyncPaiban 会 resolve 出无 code 的对象）
 * 或轮询超时耗尽（code 6，最多轮询 4 次后判定完成）
 * @param {object} res - async_paiban 响应
 * @returns {boolean}
 */
function isFailedResult(res) {
  if (!res) return true
  if (res.code === undefined || res.code === null) return true
  return Number(res.code) === 6
}

/**
 * 查找多方案对比中的失败方案
 * 只要有任意一个方案失败，整体就按失败处理，不再静默降级为「取其余方案中较优的」
 * 注意：对外只提示「排版请求失败」，不暴露具体是哪个方案失败
 * @param {Array} results - 轮询完成后的所有结果
 * @returns {{ result: object|null }|null} 第一个失败的响应，全部成功时返回 null
 */
export function findDualNestingFailure(results) {
  const result = results.find((item) => isFailedResult(item))
  return result === undefined ? null : { result }
}
